import { useCallback, useEffect, useRef, useState } from "react";

// Shared plumbing for "connect an account" OAuth flows that must leave the
// app (Meta, Google, TikTok and LinkedIn all refuse to render their consent
// screens inside an iframe). Instead of redirecting the whole tab we open the
// provider in a popup window and watch for the API's OAuth callback to send
// that popup back to one of our asset-management pages.
//
// Two detection paths run at once:
//  1. The opener polls the popup's location. While the popup is on the
//     provider's site that read throws (cross-origin) and we keep waiting;
//     once it lands back on our origin we read the URL, close the popup and
//     report the result. This works no matter what code the popup runs.
//  2. The popup page itself calls `reportOauthPopupResult` (wired up in
//     AssetManagementRouter) which posts the URL to the opener and closes.
//
// Both require the API's USER_FACING_HOST to match the host the app is
// served from; if the callback lands on a different host the popup is
// cross-origin for good and the user has to close it by hand.

export const OAUTH_POPUP_NAME = "respondology_oauth_popup";
export const OAUTH_POPUP_MESSAGE = "oauth-popup-result";

export const OAUTH_POPUP_STATUS = {
  IDLE: "idle",
  LAUNCHING: "launching",
  WAITING: "waiting",
};

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 760;
const CLOSE_GRACE_MS = 500;

/**
 * Call once on the routes an OAuth callback can land on. When the current
 * window is the popup we opened, it hands the landing URL back to the tab
 * that opened it and closes itself. Returns true when that happened.
 */
export const reportOauthPopupResult = () => {
  if (typeof window === "undefined") return false;
  if (window.name !== OAUTH_POPUP_NAME) return false;
  if (!window.opener || window.opener.closed) return false;

  try {
    window.opener.postMessage(
      {
        type: OAUTH_POPUP_MESSAGE,
        pathname: window.location.pathname,
        search: window.location.search,
      },
      window.location.origin
    );
  } catch (error) {
    return false;
  }

  window.close();
  return true;
};

export const openOauthPopup = (url) => {
  const screenLeft =
    window.screenLeft !== undefined ? window.screenLeft : window.screenX || 0;
  const screenTop =
    window.screenTop !== undefined ? window.screenTop : window.screenY || 0;
  const width = window.outerWidth || document.documentElement.clientWidth;
  const height = window.outerHeight || document.documentElement.clientHeight;
  const left = Math.max(0, screenLeft + (width - POPUP_WIDTH) / 2);
  const top = Math.max(0, screenTop + (height - POPUP_HEIGHT) / 2);

  const features = [
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
    "toolbar=no",
    "menubar=no",
    "location=yes",
    "status=no",
    "scrollbars=yes",
    "resizable=yes",
  ].join(",");

  return window.open(url, OAUTH_POPUP_NAME, features);
};

// "/asset-management/threads/accounts/12/" with landingPath
// "/asset-management/threads/accounts" -> 12
const extractLandingId = (pathname, landingPath) => {
  if (!pathname || !landingPath) return null;
  const base = landingPath.replace(/\/+$/, "");
  if (pathname !== base && !pathname.startsWith(base + "/")) return null;

  const rest = pathname.slice(base.length).split("/").filter(Boolean);
  if (rest.length === 0) return null;

  const id = parseInt(rest[0], 10);
  return Number.isNaN(id) ? null : id;
};

// Reads of a popup's location throw while it is on the provider's
// (cross-origin) site and succeed once it is back on our origin.
const readSameOriginLanding = (popup) => {
  try {
    const location = popup.location;
    if (!location || !location.href || location.href === "about:blank") {
      return null;
    }
    if (location.origin !== window.location.origin) return null;
    return { pathname: location.pathname, search: location.search };
  } catch (error) {
    return null;
  }
};

/**
 * Drives an OAuth popup from the opener side.
 *
 *   const popup = useOauthPopup({
 *     landingPath: "/asset-management/threads/accounts",
 *     onSuccess: (newId) => ...,
 *     onError: (message) => ...,
 *     onCancel: () => ...,
 *   });
 *   popup.start(() => fetchAuthorizationUrl());
 */
export default function useOauthPopup({
  landingPath,
  onSuccess,
  onError,
  onCancel,
} = {}) {
  const [status, setStatus] = useState(OAUTH_POPUP_STATUS.IDLE);

  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const resultReceivedRef = useRef(false);
  const callbacksRef = useRef({});
  callbacksRef.current = { onSuccess, onError, onCancel };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    stopPolling();
    closePopup();
    setStatus(OAUTH_POPUP_STATUS.IDLE);
  }, [stopPolling, closePopup]);

  const focus = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
    }
  }, []);

  // Shared by both detection paths; the first one to see the landing wins.
  const handleLanding = useCallback(
    ({ pathname, search }) => {
      if (resultReceivedRef.current) return;
      resultReceivedRef.current = true;

      stopPolling();
      closePopup();
      setStatus(OAUTH_POPUP_STATUS.IDLE);

      const error = new URLSearchParams(search || "").get("error");
      const callbacks = callbacksRef.current;

      if (error) {
        if (callbacks.onError) callbacks.onError(error);
      } else if (callbacks.onSuccess) {
        callbacks.onSuccess(extractLandingId(pathname, landingPath));
      }
    },
    [landingPath, stopPolling, closePopup]
  );

  // Path 2: the popup page reports its own landing URL.
  useEffect(() => {
    if (status !== OAUTH_POPUP_STATUS.WAITING) return undefined;

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== OAUTH_POPUP_MESSAGE) return;
      handleLanding({ pathname: data.pathname, search: data.search });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [status, handleLanding]);

  // Tidy up if the owning component unmounts mid-flow.
  useEffect(() => {
    return () => {
      stopPolling();
      closePopup();
    };
  }, [stopPolling, closePopup]);

  // Path 1: poll the popup (see readSameOriginLanding above).
  const watchPopup = useCallback(
    (popup) => {
      stopPolling();
      pollRef.current = setInterval(() => {
        if (popup && !popup.closed) {
          const landing = readSameOriginLanding(popup);
          if (landing) handleLanding(landing);
          return;
        }
        stopPolling();
        // Give a message posted right before the popup closed a moment to
        // arrive before treating this as a cancelled sign-in.
        setTimeout(() => {
          if (resultReceivedRef.current) return;
          popupRef.current = null;
          setStatus(OAUTH_POPUP_STATUS.IDLE);
          if (callbacksRef.current.onCancel) callbacksRef.current.onCancel();
        }, CLOSE_GRACE_MS);
      }, 500);
    },
    [stopPolling, handleLanding]
  );

  /**
   * Opens the popup and points it at the URL `getAuthorizationUrl` resolves
   * to. The window is opened synchronously on the click so browsers don't
   * block it; if they do anyway we fall back to a full-page redirect.
   * Rejects when the authorization URL can't be fetched.
   */
  const start = useCallback(
    async (getAuthorizationUrl) => {
      resultReceivedRef.current = false;
      setStatus(OAUTH_POPUP_STATUS.LAUNCHING);

      const popup = openOauthPopup("about:blank");

      try {
        const url = await getAuthorizationUrl();

        if (!popup) {
          window.location.assign(url);
          return;
        }

        popup.location.href = url;
        popupRef.current = popup;
        setStatus(OAUTH_POPUP_STATUS.WAITING);
        watchPopup(popup);
      } catch (error) {
        if (popup && !popup.closed) popup.close();
        setStatus(OAUTH_POPUP_STATUS.IDLE);
        throw error;
      }
    },
    [watchPopup]
  );

  /** Full-page redirect fallback (the pre-popup behaviour). */
  const redirect = useCallback(
    async (getAuthorizationUrl) => {
      const url = await getAuthorizationUrl();
      stopPolling();
      closePopup();
      window.location.assign(url);
    },
    [stopPolling, closePopup]
  );

  return {
    status,
    isBusy: status !== OAUTH_POPUP_STATUS.IDLE,
    isWaiting: status === OAUTH_POPUP_STATUS.WAITING,
    isLaunching: status === OAUTH_POPUP_STATUS.LAUNCHING,
    start,
    redirect,
    focus,
    cancel,
  };
}
